// resume-unblock-quota-misclassified.ts
//
// 06g R8b (dogfood 6 F-35, debate N11): a ticket `blocked` because an older
// CLI counted "You've hit your session limit · resets …" as task_failed ×3.
// On `resume`, re-read the journal: for each blocked ticket, take the
// `attempt_finished` failure records SINCE the last `ticket_state_changed →
// todo` a PERSON wrote (redirect / an earlier resume — a standalone record,
// whereas the supervisor's own `todo` is always committed together with the
// `attempt_finished`/`attempt_recovered` right after it). If every failure in
// that window reads as quota/rate-limit text, the block was a
// misclassification: counted_failures → 0 (physical_runs kept), ticket →
// `todo` with `reason: quota_misclassified_unblocked`, one notify line. One
// real failure ⇒ stays blocked (the operator still redirects, as today).
import { randomUUID } from "node:crypto";
import { join } from "node:path";
import { scanRateLimitOrQuota } from "./error-classifier.js";
import { type JournalRecord, scanJournal } from "./journal-scan-report.js";
import type { MissionStore } from "./mission-store.js";
import {
  readAttemptStateFile,
  retryStateFor,
  withRetryState,
  writeAttemptStateFile,
} from "./supervisor-attempt-state.js";

export const QUOTA_MISCLASSIFIED_UNBLOCKED = "quota_misclassified_unblocked";
const AUTO_TODO_FOLLOWERS = new Set(["attempt_finished", "attempt_recovered"]);

function isHumanTodo(records: JournalRecord[], i: number, ticket: string): boolean {
  const d = records[i]?.delta;
  if (d?.kind !== "ticket_state_changed" || d.ticket !== ticket || d.status !== "todo")
    return false;
  const next = records[i + 1]?.delta;
  return !(
    next !== undefined &&
    AUTO_TODO_FOLLOWERS.has(String(next.kind)) &&
    next.ticket === ticket
  );
}

/** Failure texts (raw_result_tail ∪ reasons) of `ticket` after its last human `todo`. */
function failureTextsSinceHumanTodo(records: JournalRecord[], ticket: string): string[] {
  let from = 0;
  for (let i = 0; i < records.length; i++) if (isHumanTodo(records, i, ticket)) from = i + 1;
  const texts: string[] = [];
  for (const { delta } of records.slice(from)) {
    if (delta.kind !== "attempt_finished" || delta.ticket !== ticket) continue;
    const category = typeof delta.category === "string" ? delta.category : undefined;
    if (category === undefined || category === "needs_decision") continue;
    const reasons = Array.isArray(delta.reasons) ? delta.reasons.map(String) : [];
    const tail = typeof delta.raw_result_tail === "string" ? [delta.raw_result_tail] : [];
    texts.push([...tail, ...reasons].join("\n"));
  }
  return texts;
}

export async function unblockQuotaMisclassifiedTickets(
  store: MissionStore,
  controlRoot: string,
  notify: ((line: string) => void) | undefined,
): Promise<string[]> {
  const blocked = Object.entries(store.state.tickets)
    .filter(([, status]) => status === "blocked")
    .map(([id]) => id);
  if (blocked.length === 0) return [];
  const { records } = await scanJournal(join(controlRoot, "journal.jsonl"));
  const unblocked: string[] = [];
  for (const ticket of blocked) {
    const texts = failureTextsSinceHumanTodo(records, ticket);
    if (texts.length === 0 || !texts.every((t) => scanRateLimitOrQuota(t) !== undefined)) continue;
    const state = await readAttemptStateFile(controlRoot);
    const retry = retryStateFor(state, ticket);
    await writeAttemptStateFile(
      controlRoot,
      withRetryState(state, ticket, { ...retry, counted_failures: 0 }),
    );
    await store.commit([
      {
        event_id: randomUUID(),
        delta: {
          kind: "ticket_state_changed",
          ticket,
          status: "todo",
          reason: QUOTA_MISCLASSIFIED_UNBLOCKED,
        } as never,
      },
    ]);
    notify?.(
      `resume: ${ticket} blocked oan vì ${texts.length} lần quota/rate-limit (session limit) — về todo, counted_failures=0 (physical_runs=${retry.physical_runs})`,
    );
    unblocked.push(ticket);
  }
  return unblocked;
}
